import { useState } from "react";
import type { Discount } from "../../api/types";
import { t } from "../../i18n";
import { discountLabel, MAX_DISCOUNTS, readDiscount, sortDiscounts } from "./general";

interface Props {
  discounts: readonly Discount[];
  /** Saves the new list at once; resolves whether it was saved. */
  onSave: (discounts: Discount[]) => Promise<boolean>;
}

const same = (a: Discount, b: Discount) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Beheer › Algemeen › "Kortingen bij het afrekenen" (Furkan, 13/10/2026: "kan je niet instellen dat elke zaak
 * die korting die daar verschijnt zelf instelt"): the buttons under Korting at the checkout. The owner takes
 * one away with ✕ and types a new one as an amount ("4", "0,50") or a percentage ("10%"); the amounts come
 * first, small to large, then the percentages.
 */
export function DiscountsCard({ discounts, onSave }: Props) {
  const text = t.general.discounts;
  const [typed, setTyped] = useState("");
  const [message, setMessage] = useState("");

  const add = async () => {
    const discount = readDiscount(typed);
    if (discount === null) {
      setMessage(text.invalid);
      return;
    }
    if (discounts.some((existing) => same(existing, discount))) {
      setMessage(text.exists(discountLabel(discount)));
      return;
    }
    if (discounts.length >= MAX_DISCOUNTS) {
      setMessage(text.full(MAX_DISCOUNTS));
      return;
    }
    setMessage("");
    if (await onSave(sortDiscounts([...discounts, discount]))) setTyped("");
  };

  return (
    <>
      <ul className="discset" aria-label={text.title}>
        {discounts.map((discount) => {
          const label = discountLabel(discount);
          return (
            <li key={label} className="disctag">
              <b className="num">{label}</b>
              <button
                type="button"
                aria-label={text.remove(label)}
                onClick={() => {
                  setMessage("");
                  void onSave(discounts.filter((existing) => !same(existing, discount)));
                }}
              >
                ✕
              </button>
            </li>
          );
        })}
      </ul>
      {discounts.length === 0 && <p className="note">{text.none}</p>}
      <form
        className="rowacts tight discnew"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          aria-label={text.new}
          placeholder={text.placeholder}
          inputMode="decimal"
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            setMessage("");
          }}
        />
        <button type="submit" className="btn small">
          {text.add}
        </button>
      </form>
      {/* the page has one alert, for what the server refuses; this one only says what was typed wrong */}
      <p className="pinmsg left" role="status">
        {message}
      </p>
    </>
  );
}
